import Axios, { AxiosError, CancelTokenSource } from 'axios';
import * as rax from 'retry-axios';
import type {
	WriteupRequest,
	WriteupResponse,
	PaymentTermsResponse,
	PaymentTermsPatchRequest,
	IncentiveObject,
	TradeInResponse,
	FiResponse,
	AccessoryRequest,
	AccessoryResponse,
	FiRequest,
	FiTerm,
	IncentiveRequest,
	RebatePayloadType,
	RebateResponse,
	RebateUpdateRequest,
	ContactRequest,
	AppointmentRequest,
	DatesAppointmentData,
	TimeslotsAppointmentData,
	AppointmentResponseData, TestDriveAppointmentData, ShiftDigitalData, GtmData, TealiumResponse,
} from '@/types/writeup';
import { APP_CONFIG } from '@/common';
import { PrecisePriceConfig } from '@/entities/config';
import { TradeIn } from '@/types/tradepending';

const axios = Axios.create({
	baseURL: `${APP_CONFIG.API_URL}writeup`,
});

axios.defaults.raxConfig = {
	instance: axios,
	retry: 0,
	noResponseRetries: 0,
	retryDelay: 1000,
	statusCodesToRetry: [
		[422, 422], // Client errors
		[429, 429], // "Too many redirects"
		[500, 599], // Server errors
	],
	httpMethodsToRetry: ['GET'],
};

rax.attach(axios);

export class WriteupService {
	websiteId: string

	termsCancelToken: null | CancelTokenSource = null;

	incentivesCancelToken: null | CancelTokenSource = null;

	constructor({ apiKey, websiteId, ppSSOKey }: PrecisePriceConfig) {
		this.websiteId = websiteId;

		axios.defaults.headers['X-Api-Key'] = apiKey;
		axios.defaults.headers['X-Requested-With'] = 'XMLHttpRequest';
		if (ppSSOKey !== undefined) {
			axios.defaults.headers['X-Pp-Key'] = ppSSOKey;
		}

		// axios.defaults.withCredentials = true;
	}

	public create(vehicleVin: string): Promise<WriteupResponse> {
		const data = {
			site: {
				credentials: {
					websiteId: this.websiteId,
				},
			},
			vehicle: {
				vin: vehicleVin,
			},
		};

		return axios.post('', data)
			.then(res => res.data);
	}

	public get(id: string): Promise<WriteupResponse> {
		return axios.get(`/${id}`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}

				throw error;
			});
	}

	public patch(id: number, data: Partial<WriteupRequest>): Promise<WriteupResponse> {
		return axios.patch(`/${id}`, data)
			.then(res => res.data);
	}

	public getPaymentTerms(id: number, creditScore?: number, downPayment?: number): Promise<PaymentTermsResponse> {
		let query = creditScore ? `creditScore=${creditScore}` : '';

		query += downPayment ? `&downPayment=${downPayment}` : '';

		return axios.get(`/${id}/paymentTerms?${query}`)
			.then(res => res.data);
	}

	public patchPaymentTerms(id: number, paymentTerms: Partial<PaymentTermsPatchRequest>): Promise<WriteupResponse> {
		if (this.termsCancelToken) {
			this.termsCancelToken.cancel('cancel');
		}

		this.termsCancelToken = Axios.CancelToken.source();
		return axios.patch(`/${id}/paymentTerms`, paymentTerms, { cancelToken: this.termsCancelToken.token })
			.then(res => res.data);
	}

	public getIncentives(id: number, creditScore?: number, downPayment?: number): Promise<IncentiveObject[]> {
		let query = creditScore ? `creditScore=${creditScore}` : '';

		query += downPayment ? `&downPayment=${downPayment}` : '';

		return axios.get(`/${id}/incentives?${query}`)
			.then(res => res.data);
	}

	public getTradeIn(id: number): Promise<TradeInResponse> {
		return axios.get(`/${id}/tradein`);
	}

	public getOptions(id: number): Promise<FiResponse[]> {
		return axios.get(`/${id}/fi`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public updateOptions(id: number, selectedOptions: Array<FiRequest>): Promise<WriteupResponse> {
		return axios.put(`/${id}/fi`, selectedOptions)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getAccessories(id: number): Promise<AccessoryResponse[]> {
		return axios.get(`/${id}/accessory`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public updateAccessories(id: number, selectedAccessories: Array<AccessoryRequest>): Promise<WriteupResponse> {
		return axios.put(`/${id}/accessory`, selectedAccessories)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getFiTerms(writeupId: number | undefined, optionId: number | undefined): Promise<FiTerm[]> {
		return axios.get(`/${writeupId}/fi/${optionId}/terms`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404 || error?.response?.status === 500) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public saveSelectedOffer(id: number, payload: IncentiveRequest): Promise<WriteupResponse> {
		if (this.incentivesCancelToken) {
			this.incentivesCancelToken.cancel('cancel');
		}

		this.incentivesCancelToken = Axios.CancelToken.source();

		return axios.put(`/${id}/incentives`, payload, { cancelToken: this.incentivesCancelToken.token })
			.then(res => res.data);
	}

	public getRebates(payload: RebatePayloadType): Promise<RebateResponse[]> {
		let query = payload.source ? `source=${payload.source}` : '';

		query += payload.creditScore ? `&creditScore=${payload.creditScore}` : '';
		query += payload.downPayment ? `&downPayment=${payload.downPayment}` : '';

		return axios.get(`/${payload.id}/rebates?${query}`)
			.then(res => res.data);
	}

	public putRebates(writeupId: number | undefined, rebates: RebateUpdateRequest[]): Promise<WriteupResponse> {
		return axios.put(`/${writeupId}/rebates`, rebates)
			.then(res => res.data);
	}

	public saveTradeIn(id: number, tradeInOptions: TradeIn): Promise<WriteupResponse> {
		return axios.put(`/${id}/tradein`, tradeInOptions)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public saveContactData(writeupId: number, data: ContactRequest): Promise<WriteupResponse> {
		return axios.put(`/${writeupId}/save`, data)
			.then(res => res.data);
	}

	public sendAppointment(dealerId: number, data: AppointmentRequest): Promise<AppointmentResponseData> {
		return axios.post(`${APP_CONFIG.API_URL}dealer/${dealerId}/appointment/save`, data)
			.then(res => res.data);
	}

	public getDatesAppointment(dealerId: number, type: number): Promise<DatesAppointmentData> {
		return axios.get(`${APP_CONFIG.API_URL}dealer/${dealerId}/appointment/days?type=${type}`)
			.then(res => res.data);
	}

	public getTimeslotsAppointment(dealerId: number, type: number, date: string): Promise<TimeslotsAppointmentData> {
		return axios.get(`${APP_CONFIG.API_URL}dealer/${dealerId}/appointment/timeslots?type=${type}&date=${date}`)
			.then(res => res.data);
	}

	public getTestDriveAppointmentData(dealerId: number, writeupId: number): Promise<TestDriveAppointmentData> {
		return axios.get(`${APP_CONFIG.API_URL}dealer/${dealerId}/appointment?writeupId=${writeupId}`)
			.then(res => res.data);
	}

	public getTaxesAndFees(writeupId: number|undefined) {
		return axios.get(`/${writeupId}/taxes`)
			.then(res => res.data);
	}

	public getShiftDigitalData(writeupId: number|undefined): Promise<ShiftDigitalData> {
		return axios.get(`${APP_CONFIG.API_URL}analytics/shiftDigital/${writeupId}`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getGtmData(writeupId: number|undefined): Promise<GtmData> {
		return axios.get(`${APP_CONFIG.API_URL}analytics/googleTagManager/${writeupId}`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getTealiumData(writeupId: string|undefined): Promise<TealiumResponse> {
		return axios.get(`${APP_CONFIG.API_URL}analytics/tealium/${writeupId}`)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public addDealerDiscount(writeupId: number|undefined): Promise<WriteupResponse> {
		return axios.get(`/${writeupId}/addDealerDiscount`)
			.then(res => res.data);
	}
}
